<template>
  <div class="reset-page">
    <!-- Top navbar -->
    <div class="reset-nav">
      <div class="reset-nav__logo">
        <!-- Dark-neutral-inverted variant on the dark navbar — see the note in
             PageGuestHome.vue. -->
        <img :src="logoSrc" alt="taxPOD" class="reset-nav__logo-img" />
      </div>

      <div class="reset-nav__spacer"></div>

      <!-- Dark mode toggle — same control as the console navbar and the login
           page. A native `title` stands in for the console's v-tooltip: this
           page is inside a `zoom: 1.1` wrapper, and a detached Vuetify overlay
           is positioned from an unzoomed coordinate space. -->
      <button
        class="darkmode-toggle"
        type="button"
        :title="isDarkMode ? 'Light Mode' : 'Dark Mode'"
        :aria-label="isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'"
        :aria-pressed="String(isDarkMode)"
        @click="toggleDarkMode()"
      >
        <v-icon size="20" :color="isDarkMode ? 'var(--app-warning)' : 'var(--app-text-muted)'">
          {{ isDarkMode ? 'mdi-white-balance-sunny' : 'mdi-moon-waning-crescent' }}
        </v-icon>
      </button>
    </div>

    <!-- Main content -->
    <div class="reset-main">
      <!-- Left panel -->
      <div class="reset-left">
        <div class="reset-left__bg">
          <div class="geo geo--1"></div>
          <div class="geo geo--2"></div>
          <div class="geo geo--3"></div>
          <div class="geo geo--4"></div>
        </div>
        <div class="reset-left__content">
          <h1 class="reset-left__title">
            Simplify Your<br />
            HRDC Claims,<br />
            From Submission<br />
            to Approval
          </h1>
        </div>
      </div>

      <!-- Right panel -->
      <div class="reset-right">
        <div class="reset-right__wave">
          <svg viewBox="0 0 600 800" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <path class="reset-wave__path" d="M600 800V0C600 0 580 200 500 350C420 500 300 550 250 650C200 750 300 800 600 800Z"/>
            <path class="reset-wave__path reset-wave__path--soft" d="M600 800V100C600 100 560 300 480 420C400 540 320 580 280 680C240 780 350 800 600 800Z"/>
          </svg>
        </div>

        <!-- Reset card -->
        <div class="reset-card">
          <button class="reset-card__back" @click="redirectToLoginPage()">
            <v-icon size="18" color="var(--app-text-muted)">mdi-arrow-left</v-icon>
            <span>Back to Login</span>
          </button>

          <div class="reset-card__icon-wrap">
            <v-icon size="28" color="var(--app-accent)">mdi-lock-reset</v-icon>
          </div>

          <h2 class="reset-card__title">Reset Password</h2>
          <p class="reset-card__subtitle">Enter your email address and we'll send you a recovery link</p>

          <transition name="fade">
            <div v-if="api.isError" class="reset-alert reset-alert--error">
              <v-icon size="18" color="var(--app-danger-fg)" class="reset-alert__icon">mdi-alert-circle-outline</v-icon>
              <span>{{ api.error }}</span>
            </div>
          </transition>

          <!-- Email -->
          <div class="field">
            <label class="field__label">Email Address</label>
            <div class="field__input-wrap">
              <v-icon size="18" color="var(--app-text-faint)" class="field__icon">mdi-email-outline</v-icon>
              <input
                type="email"
                class="field__input field__input--icon"
                placeholder="name@email.com"
                v-model="form.email"
                @keydown.enter.prevent="fetch"
              />
            </div>
          </div>

          <!-- Submit Button -->
          <button
            class="reset-btn"
            :disabled="api.isLoading"
            @click="fetch()"
          >
            <span v-if="!api.isLoading">Send Recovery Email</span>
            <span v-else class="reset-btn__loading">
              <v-progress-circular indeterminate size="18" width="2" color="var(--app-login-btn-fg)"></v-progress-circular>
              Sending...
            </span>
          </button>
        </div>
      </div>
    </div>

    <AppFooter />
  </div>
</template>

<script>
import Api from "@/objects/api";
import HrdcData from "@/services/hrdc/objects/globalData";
import { themeState, toggleDark } from "@/plugins/theme";

export default {
  computed: {
    // The app-wide theme singleton, same state the login page and the console
    // navbar toggle.
    isDarkMode() {
      return themeState.dark;
    },
    logoSrc() {
      return this.isDarkMode
        ? "/images/taxpod_logo_dark.png"
        : "/images/taxpod_logo.png";
    },
  },
  data: () => ({
    form: {
      email: null,
    },
    api: new Api(),
    hrdcData: new HrdcData(),
  }),
  created() {
    this.api.setMethod(`POST`);
    this.api.setUrl(`${this.$service.sso}/v1/en/auth/login/password/reset`);
    this.api.setCallbackCompleted((response) => {
      if (response.status) {
        this.redirectToLoginPage(response.message);
      }
    });
  },
  methods: {
    fetch() {
      this.api.setParams(this.form);
      this.api.fetch();
    },
    redirectToLoginPage(message = null) {
      this.$router.push({
        name: "PageGuestHome",
        params: {
          message,
        },
      });
    },
    toggleDarkMode() {
      toggleDark();
    },
  },
};
</script>

<style scoped>
/* ===== DARK MODE =====
   Hand-authored, like PageGuestHome.vue — see the long note there. The short
   version: the generated companion set only ever named the navbar, the card and
   the fields, so .reset-page / .reset-left / .reset-right / the wave kept their
   light values and dark mode rendered as a dark card on a bright page. The
   panel, geo shapes, wave and CTA gradient are themed at their BASE rule via
   --app-login-* and need no override here. Token references only — no literal
   hexes. */

.dark-mode .reset-page {
  background: var(--app-page);
}

.dark-mode .reset-nav {
  background: var(--app-navbar);
  border-bottom: 1px solid var(--app-border);
}

.dark-mode .darkmode-toggle:hover {
  background-color: var(--app-hover);
}

.dark-mode .reset-right {
  background: var(--app-page);
}

.dark-mode .reset-card {
  background: var(--app-card);
  border-color: var(--app-border);
  box-shadow: 0 2px 12px var(--app-shadow-mid),
    inset 0 1px 0 var(--app-inner-highlight);
}

.dark-mode .reset-card__back {
  color: var(--app-text-muted);
}

.dark-mode .reset-card__back:hover {
  color: var(--app-accent);
}

/* The pale accent tile behind the header icon: a solid #f0f7ff would be a white
   block on the card, so dark uses the translucent accent wash instead. */
.dark-mode .reset-card__icon-wrap {
  background: var(--app-accent-soft);
}

.dark-mode .reset-card__title {
  color: var(--app-text);
}

.dark-mode .reset-card__subtitle {
  color: var(--app-text-faint);
}

.dark-mode .reset-alert--error {
  background: var(--app-danger-bg);
  border-color: var(--app-danger-border);
  color: var(--app-danger-fg);
}

.dark-mode .field__label {
  color: var(--app-text);
}

/* A field sits one step ABOVE the card it is on — --app-card here would make
   each input a hole in the card rather than a target to click into. */
.dark-mode .field__input {
  background: var(--app-field);
  border-color: var(--app-field-border);
  color: var(--app-text);
}

.dark-mode .field__input::placeholder {
  color: var(--app-field-placeholder);
}

.dark-mode .field__input:focus {
  border-color: var(--app-accent);
  box-shadow: 0 0 0 3px var(--app-ring);
}

/* ===== end dark mode ===== */

/* ===== PAGE ===== */
.reset-page {
  /* `zoom: 1.1` below scales this box after layout, so a plain 100vh paints at
     110vh and the bottom tenth — the footer — falls off screen behind
     `overflow: hidden`. Dividing by the zoom makes the painted height 100vh. */
  height: calc(100vh / 1.1);
  display: flex;
  flex-direction: column;
  font-family: 'Lato', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
  background: #f0f4f8;
  zoom: 1.1;
  overflow: hidden;
}

/* ===== NAVBAR ===== */
.reset-nav {
  height: 56px;
  background: #fff;
  display: flex;
  align-items: center;
  padding: 0 32px;
  border-bottom: 1px solid #e8eaed;
  flex-shrink: 0;
  z-index: 10;
}

.reset-nav__logo {
  display: flex;
  align-items: center;
}

.reset-nav__logo-img {
  height: 32px;
  object-fit: contain;
}

.reset-nav__spacer {
  flex: 1;
}

/* Mirrors the console navbar's .darkmode-toggle so the control is the same
   object everywhere. */
.darkmode-toggle {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.15s ease;
}

.darkmode-toggle:hover {
  background-color: rgba(0, 0, 0, 0.06);
}

/* ===== MAIN ===== */
.reset-main {
  flex: 1;
  display: flex;
  min-height: 0;
}

/* ===== LEFT PANEL ===== */
.reset-left {
  flex: 0 0 42%;
  background: var(--app-login-panel);
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
}

.reset-left__bg {
  position: absolute;
  inset: 0;
}

.geo {
  position: absolute;
  background: var(--app-login-geo);
}

.geo--1 {
  width: 300px;
  height: 300px;
  top: -50px;
  left: -80px;
  transform: rotate(45deg);
  border: 1px solid var(--app-login-geo-border);
}

.geo--2 {
  width: 200px;
  height: 200px;
  top: 30%;
  left: 25%;
  transform: rotate(30deg);
  border: 1px solid var(--app-login-geo-border);
}

.geo--3 {
  width: 250px;
  height: 250px;
  bottom: -60px;
  left: 10%;
  transform: rotate(60deg);
  border: 1px solid var(--app-login-geo-border);
}

.geo--4 {
  width: 150px;
  height: 150px;
  top: 15%;
  right: -30px;
  transform: rotate(15deg);
  border: 1px solid var(--app-login-geo-border);
}

.reset-left__content {
  position: relative;
  z-index: 1;
  padding: 60px;
}

.reset-left__title {
  font-size: 1.8rem;
  font-weight: 800;
  color: var(--app-login-panel-fg);
  line-height: 1.35;
  margin: 0;
}

/* ===== RIGHT PANEL ===== */
.reset-right {
  flex: 1;
  background: #f0f4f8;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  padding: 40px;
}

.reset-right__wave {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 60%;
  height: 100%;
  pointer-events: none;
}

.reset-right__wave svg {
  width: 100%;
  height: 100%;
}

/* Painted from CSS rather than a `fill` attribute so the wave follows the
   theme like everything else. */
.reset-wave__path {
  fill: var(--app-login-wave);
}

.reset-wave__path--soft {
  fill: var(--app-login-wave-soft);
}

/* ===== RESET CARD ===== */
.reset-card {
  width: 100%;
  max-width: 400px;
  background: #fff;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  border: 1px solid #e4e8ec;
  position: relative;
  z-index: 1;
}

.reset-card__back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  cursor: pointer;
  font-family: 'Lato', sans-serif;
  font-size: 0.8rem;
  font-weight: 600;
  color: #64748b;
  padding: 4px 0;
  margin-bottom: 24px;
  transition: color 0.15s;
}

.reset-card__back:hover {
  color: #0ea5e9;
}

.reset-card__icon-wrap {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: #f0f7ff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 18px;
}

.reset-card__title {
  font-size: 1.3rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 6px;
}

.reset-card__subtitle {
  font-size: 0.82rem;
  color: #94a3b8;
  margin: 0 0 24px;
  font-weight: 400;
  line-height: 1.5;
}

/* ===== ALERT ===== */
.reset-alert {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 0.82rem;
  font-weight: 500;
}

.reset-alert--error {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
}

.reset-alert__icon {
  flex-shrink: 0;
}

/* ===== FORM FIELD ===== */
.field {
  margin-bottom: 24px;
}

.field__label {
  display: block;
  font-size: 0.82rem;
  font-weight: 600;
  color: #334155;
  margin-bottom: 6px;
}

.field__input-wrap {
  position: relative;
}

.field__icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
}

.field__input {
  width: 100%;
  padding: 12px 14px;
  border: 1.5px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.88rem;
  font-family: 'Lato', sans-serif;
  color: #1e293b;
  background: #fff;
  transition: all 0.2s ease;
  outline: none;
}

.field__input--icon {
  padding-left: 42px;
}

.field__input::placeholder {
  color: #b0b8c1;
}

.field__input:focus {
  border-color: #60b8e0;
  box-shadow: 0 0 0 3px rgba(96, 184, 224, 0.1);
}

/* ===== BUTTON ===== */
.reset-btn {
  width: 100%;
  padding: 13px;
  background: var(--app-login-btn);
  color: var(--app-login-btn-fg);
  border: none;
  border-radius: 8px;
  font-size: 0.92rem;
  font-weight: 700;
  font-family: 'Lato', sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
  letter-spacing: 0.3px;
}

.reset-btn:hover {
  background: var(--app-login-btn-hover);
  box-shadow: 0 4px 14px var(--app-login-btn-glow);
}

.reset-btn:disabled {
  opacity: 0.7;
  cursor: wait;
}

.reset-btn__loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

/* ===== MOBILE ===== */
@media (max-width: 768px) {
  .reset-main {
    flex-direction: column;
  }

  .reset-left {
    flex: none;
    min-height: 160px;
  }

  .reset-left__content {
    padding: 32px 28px;
  }

  .reset-left__title {
    font-size: 1.2rem;
  }

  .reset-right {
    padding: 28px 20px;
  }

  .reset-card {
    max-width: 100%;
    padding: 24px;
  }
}

@media (min-width: 769px) and (max-width: 1024px) {
  .reset-left {
    flex: 0 0 36%;
  }

  .reset-left__content {
    padding: 40px;
  }

  .reset-left__title {
    font-size: 1.4rem;
  }
}
</style>
